import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/lib/supabase";
import { uploadImage } from "@/lib/upload";
import { useUser } from "@/context/UserContext";
import { SALES_SPECIALISTS } from "@/lib/salesSpecialists";

interface Department {
  id: string;
  name: string;
  icon: string;
}

interface Conversation {
  id: string;
  employee_profile_id: string;
  employee_name: string;
  sales_specialist?: string;
  department_id: string;
  status: "waiting" | "processing" | "resolved" | "closed";
  last_message_at: string | null;
  last_message_preview: string | null;
  unread_manager_count: number;
  assigned_manager_id?: string | null;
  assigned_manager_name?: string | null;
  assigned_at?: string | null;
}

interface ChatMessage {
  id: string;
  conversation_id: string;
  sender_profile_id: string;
  sender_type: "employee" | "department";
  message_type: "text" | "image" | "file" | "system";
  content: string;
  is_edited: boolean;
  is_deleted: boolean;
  reply_to_message_id: string | null;
  created_at: string;
  sender_name?: string;
  department_name?: string;
}

type MobileView = "departments" | "conversations" | "chat";

function formatTime(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  if (d.getDate() === now.getDate() && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()) {
    return d.toLocaleTimeString("zh-TW", { hour: "2-digit", minute: "2-digit", hour12: false });
  }
  return d.toLocaleDateString("zh-TW", { month: "2-digit", day: "2-digit" }) + " " +
    d.toLocaleTimeString("zh-TW", { hour: "2-digit", minute: "2-digit", hour12: false });
}

const STATUS_LABELS: Record<string, string> = {
  waiting: "等待處理",
  processing: "處理中",
  resolved: "已解決",
  closed: "已關閉",
};

const STATUS_COLORS: Record<string, string> = {
  waiting: "bg-accent-100 text-accent-700",
  processing: "bg-primary-100 text-primary-700",
  resolved: "bg-secondary-100 text-secondary-700",
  closed: "bg-background-200 text-foreground-500",
};

export default function ManagerCenterPage() {
  const { profileId, userName, isAdmin, userRole } = useUser();
  const isManager = isAdmin || userRole === "manager";
  const [departments, setDepartments] = useState<Department[]>([]);
  const [activeDept, setActiveDept] = useState<Department | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConv, setSelectedConv] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [specialistFilter, setSpecialistFilter] = useState("all");
  const [loadingDepts, setLoadingDepts] = useState(true);
  const [loadingConvs, setLoadingConvs] = useState(false);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [expandedImages, setExpandedImages] = useState<Set<string>>(new Set());
  const [deptUnreadCounts, setDeptUnreadCounts] = useState<Record<string, number>>();
  const [mobileView, setMobileView] = useState<MobileView>("departments");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const lastMessageTimeRef = useRef<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const toggleImageExpand = (msgId: string) => {
    setExpandedImages((prev) => {
      const next = new Set(prev);
      if (next.has(msgId)) {
        next.delete(msgId);
      } else {
        next.add(msgId);
      }
      return next;
    });
  };

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  // Sync lastMessageTimeRef with latest message timestamp
  useEffect(() => {
    if (messages.length > 0) {
      lastMessageTimeRef.current = messages[messages.length - 1].created_at;
    } else {
      lastMessageTimeRef.current = null;
    }
  }, [messages]);

  // Upload image via multipart to local backend (replaces base64)
  // See @/lib/upload

  // Handle image upload
  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedConv || !profileId || !activeDept) return;

    if (!file.type.startsWith("image/")) {
      showToast("請選擇圖片檔案");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      showToast("圖片大小不能超過 2MB");
      return;
    }

    try {
      // multipart upload → URL (replaces base64)
      const imageUrl = await uploadImage(file);

      const tempId = "temp-img-" + Date.now();
      const tempMsg: ChatMessage = {
        id: tempId,
        conversation_id: selectedConv.id,
        sender_profile_id: profileId,
        sender_type: "department",
        message_type: "image",
        content: imageUrl,
        is_edited: false,
        is_deleted: false,
        reply_to_message_id: null,
        created_at: new Date().toISOString(),
        sender_name: userName || "主管",
      };
      setMessages((prev) => [...prev, tempMsg]);

      const { data: inserted, error } = await supabase.from("chat_messages").insert({
        conversation_id: selectedConv.id,
        sender_profile_id: profileId,
        sender_type: "department",
        message_type: "image",
        content: imageUrl,
      }).select("id").single();

      if (error) {
        showToast("圖片發送失敗：" + error.message);
        setMessages((prev) => prev.filter((m) => m.id !== tempId));
      } else if (inserted) {
        setMessages((prev) => prev.map((m) => m.id === tempId ? { ...m, id: inserted.id } : m));
      }
    } catch {
      showToast("圖片處理失敗");
    }

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Fetch departments this manager manages
  const fetchDepartments = useCallback(async () => {
    if (!profileId) return;
    setLoadingDepts(true);
    try {
      let deptIds: string[] = [];

      if (isAdmin) {
        const { data } = await supabase.from("departments").select("id");
        deptIds = (data || []).map((d) => d.id);
      } else {
        const { data } = await supabase
          .from("manager_departments")
          .select("department_id")
          .eq("manager_profile_id", profileId);
        deptIds = (data || []).map((d) => d.department_id);
      }

      if (deptIds.length === 0) {
        setDepartments([]);
        setLoadingDepts(false);
        return;
      }

      const { data } = await supabase
        .from("departments")
        .select("id, name, icon")
        .in("id", deptIds)
        .order("display_order", { ascending: true });

      setDepartments(data || []);

      // Fetch unread counts for each department
      const { data: unreadData } = await supabase
        .from("department_conversations")
        .select("department_id, unread_manager_count")
        .in("department_id", deptIds);

      const counts: Record<string, number> = {};
      (unreadData || []).forEach((row: any) => {
        counts[row.department_id] = (counts[row.department_id] || 0) + (row.unread_manager_count || 0);
      });
      setDeptUnreadCounts(counts);
      // 即時廣播給 AppShell 導航徽章
      const allTotal = Object.values(counts).reduce((s, n) => s + (n || 0), 0);
      window.dispatchEvent(new CustomEvent("manager:unread-sync", { detail: { total: allTotal } }));
    } catch (err: any) {
      showToast("載入部門失敗: " + (err?.message || ""));
    } finally {
      setLoadingDepts(false);
    }
  }, [profileId, isAdmin]);

  // Fetch conversations for active department (direct query)
  const fetchConversations = useCallback(async (deptId: string) => {
    setLoadingConvs(true);
    try {
      const { data, error } = await supabase
        .from("department_conversations")
        .select(`
          id, employee_profile_id, department_id, status, sales_specialist,
          last_message_at, last_message_preview, unread_manager_count,
          employee:profiles!department_conversations_employee_profile_id_fkey(display_name),
          assignments:conversation_assignments(is_active, assigned_to_profile_id, assigned_at)
        `)
        .eq("department_id", deptId)
        .order("last_message_at", { ascending: false });

      if (error) throw error;

      const enriched: Conversation[] = (data || []).map((row: any) => {
        const activeAssign = (row.assignments || []).find((a: any) => a.is_active);
        return {
          id: row.id,
          employee_profile_id: row.employee_profile_id,
          employee_name: row.employee?.display_name || "未知員工",
          sales_specialist: row.sales_specialist || "",
          department_id: row.department_id,
          status: row.status,
          last_message_at: row.last_message_at,
          last_message_preview: row.last_message_preview,
          unread_manager_count: row.unread_manager_count || 0,
          assigned_manager_id: activeAssign?.assigned_to_profile_id,
          assigned_at: activeAssign?.assigned_at,
        };
      });

      setConversations(enriched);
    } catch (err: any) {
      showToast("載入對話列表失敗: " + (err?.message || ""));
    } finally {
      setLoadingConvs(false);
    }
  }, []);

  // Fetch messages for selected conversation (direct query)
  const fetchMessages = useCallback(async (convId: string) => {
    setLoadingMsgs(true);
    try {
      const { data, error } = await supabase
        .from("chat_messages")
        .select(`
          id, conversation_id, sender_profile_id, sender_type, message_type,
          content, is_edited, is_deleted, reply_to_message_id, created_at,
          sender:profiles!chat_messages_sender_profile_id_fkey(display_name)
        `)
        .eq("conversation_id", convId)
        .eq("is_deleted", false)
        .order("created_at", { ascending: true });

      if (error) throw error;

      const enriched: ChatMessage[] = (data || []).map((row: any) => ({
        id: row.id,
        conversation_id: row.conversation_id,
        sender_profile_id: row.sender_profile_id,
        sender_type: row.sender_type,
        message_type: row.message_type,
        content: row.content,
        is_edited: row.is_edited,
        is_deleted: row.is_deleted,
        reply_to_message_id: row.reply_to_message_id,
        created_at: row.created_at,
        sender_name: row.sender?.display_name,
      }));

      setMessages(enriched);
    } catch (err: any) {
      showToast("讀取訊息失敗: " + (err?.message || ""));
    } finally {
      setLoadingMsgs(false);
    }
  }, []);

  // Mark messages as read
  const markAsRead = useCallback(async (convId: string) => {
    if (!profileId) return;
    try {
      const { data: unreadMsgs } = await supabase
        .from("chat_messages")
        .select("id")
        .eq("conversation_id", convId)
        .neq("sender_profile_id", profileId);

      // 只有存在「他人訊息」才需要寫已讀記錄；
      // 但歸零 unread_manager_count 必須無條件執行 ——
      // 否則當對話裡全是自己發的訊息（自己以 employee 身分發、system 訊息）時，
      // early return 會跳過歸零，徽章永遠消不掉。
      if (unreadMsgs && unreadMsgs.length > 0) {
        const reads = unreadMsgs.map((m) => ({
          message_id: m.id,
          reader_profile_id: profileId,
        }));

        await supabase.from("chat_message_reads").upsert(reads, {
          onConflict: "message_id, reader_profile_id",
          ignoreDuplicates: true,
        });
      }

      // Reset manager unread count
      await supabase
        .from("department_conversations")
        .update({ unread_manager_count: 0 })
        .eq("id", convId);

      // 即時更新本地對話列表與導航徽章
      setConversations((prev) =>
        prev.map((c) =>
          c.id === convId ? { ...c, unread_manager_count: 0 } : c
        )
      );
    } catch {
      // Silently fail
    }
  }, [profileId]);

  // Fetch only new messages since last known timestamp (incremental polling)
  const fetchNewMessages = useCallback(async (convId: string) => {
    const { data: deleted } = await supabase.from("chat_messages")
      .select("id").eq("conversation_id", convId).eq("is_deleted", true);
    if (deleted?.length) {
      const deletedIds = new Set(deleted.map((item: { id: string }) => item.id));
      setMessages((prev) => prev.some((item) => deletedIds.has(item.id))
        ? prev.filter((item) => !deletedIds.has(item.id)) : prev);
    }
    const since = lastMessageTimeRef.current;
    if (!since) return;

    try {
      const { data, error } = await supabase
        .from("chat_messages")
        .select(`
          id, conversation_id, sender_profile_id, sender_type, message_type,
          content, is_edited, is_deleted, reply_to_message_id, created_at,
          sender:profiles!chat_messages_sender_profile_id_fkey(display_name)
        `)
        .eq("conversation_id", convId)
        .eq("is_deleted", false)
        .gt("created_at", since)
        .order("created_at", { ascending: true });

      if (error || !data || data.length === 0) return;

      const enriched: ChatMessage[] = (data || []).map((row: any) => ({
        id: row.id,
        conversation_id: row.conversation_id,
        sender_profile_id: row.sender_profile_id,
        sender_type: row.sender_type,
        message_type: row.message_type,
        content: row.content,
        is_edited: row.is_edited,
        is_deleted: row.is_deleted,
        reply_to_message_id: row.reply_to_message_id,
        created_at: row.created_at,
        sender_name: row.sender?.display_name,
      }));

      setMessages((prev) => {
        const existingIds = new Set(prev.map((m) => m.id));
        const trulyNew = enriched.filter((m) => !existingIds.has(m.id));
        if (trulyNew.length === 0) return prev;
        return [...prev, ...trulyNew];
      });

      // Also update conversation in the list: preview + time + unread count
      const lastMsg = enriched[enriched.length - 1];
      const preview = lastMsg.message_type === "text"
        ? lastMsg.content.slice(0, 50)
        : lastMsg.message_type === "image"
          ? "[圖片]"
          : "[檔案]";
      const employeeNewCount = enriched.filter(
        (m) => m.sender_type === "employee" && m.sender_profile_id !== profileId
      ).length;

      setConversations((prev) =>
        prev.map((c) =>
          c.id === convId
            ? {
                ...c,
                last_message_at: lastMsg.created_at,
                last_message_preview: preview,
                // fetchNewMessages 只跑在「目前選中」的對話上：新訊息正在被觀看，
                // 未讀維持 0，不可累加（否則側欄徽章會無限膨脹）
                unread_manager_count: employeeNewCount > 0 ? 0 : c.unread_manager_count,
              }
            : c
        )
      );

      if (employeeNewCount > 0) {
        // DB 的 unread_manager_count 已被 insert trigger +1 —— 正在觀看，歸零回去
        //（與 department-chat 員工端的歸零行為對齊）
        supabase
          .from("department_conversations")
          .update({ unread_manager_count: 0 })
          .eq("id", convId)
          .then(() => {});
        // 補寫已讀記錄（polling 路徑與 realtime handler 對齊）
        if (profileId) {
          const reads = enriched
            .filter((m) => m.sender_type === "employee" && m.sender_profile_id !== profileId)
            .map((m) => ({ message_id: m.id, reader_profile_id: profileId }));
          if (reads.length > 0) {
            supabase
              .from("chat_message_reads")
              .upsert(reads, {
                onConflict: "message_id, reader_profile_id",
                ignoreDuplicates: true,
              })
              .then(() => {});
          }
        }
      }
    } catch {
      // Best-effort polling, silently ignore errors
    }
  }, [profileId]);

  // Handle status change
  const handleStatusChange = async (newStatus: string) => {
    if (!selectedConv) return;
    try {
      const { error } = await supabase
        .from("department_conversations")
        .update({ status: newStatus })
        .eq("id", selectedConv.id);

      if (error) throw error;
      setSelectedConv({ ...selectedConv, status: newStatus as any });
      showToast(`狀態已更新為「${STATUS_LABELS[newStatus]}」`);
      if (activeDept) fetchConversations(activeDept.id);
    } catch (err: any) {
      showToast("更新狀態失敗: " + (err?.message || ""));
    }
  };

  // Handle assign/unassign
  const handleAssign = async () => {
    if (!selectedConv || !profileId) return;
    try {
      // Deactivate existing assignments
      await supabase
        .from("conversation_assignments")
        .update({ is_active: false, unassigned_at: new Date().toISOString() })
        .eq("conversation_id", selectedConv.id)
        .eq("is_active", true);

      // Create new assignment
      const { error } = await supabase.from("conversation_assignments").insert({
        conversation_id: selectedConv.id,
        assigned_to_profile_id: profileId,
        assigned_by_profile_id: profileId,
        is_active: true,
      });

      if (error) throw error;

      // Update status to processing
      await supabase
        .from("department_conversations")
        .update({ status: "processing" })
        .eq("id", selectedConv.id);

      setSelectedConv({
        ...selectedConv,
        status: "processing",
        assigned_manager_id: profileId,
        assigned_manager_name: userName || "我",
      });
      showToast("已接手此案件");
      if (activeDept) fetchConversations(activeDept.id);
    } catch (err: any) {
      showToast("接手失敗: " + (err?.message || ""));
    }
  };

  // Handle send message
  const handleDeleteMessage = async (msg: ChatMessage) => {
    if (!isManager || deletingId || msg.id.startsWith("temp-")) return;
    if (!window.confirm("確定刪除這則訊息？刪除後員工端也會隱藏此訊息。")) return;
    setDeletingId(msg.id);
    try {
      const { error } = await supabase.functions.invoke("delete-chat-message", { body: { message_id: msg.id } });
      if (error) throw new Error(error.message);
      setMessages((prev) => prev.filter((item) => item.id !== msg.id));
      if (activeDept) await fetchConversations(activeDept.id);
      showToast("訊息已刪除");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "刪除失敗，請稍後再試");
    } finally {
      setDeletingId(null);
    }
  };
  const handleSend = async () => {
    if (!inputText.trim() || !selectedConv || !profileId || !activeDept) return;

    const content = inputText.trim();
    setInputText("");

    const tempMsg: ChatMessage = {
      id: "temp-" + Date.now(),
      conversation_id: selectedConv.id,
      sender_profile_id: profileId,
      sender_type: "department",
      message_type: "text",
      content,
      is_edited: false,
      is_deleted: false,
      reply_to_message_id: null,
      created_at: new Date().toISOString(),
      sender_name: userName || "主管",
      department_name: activeDept.name,
    };
    setMessages((prev) => [...prev, tempMsg]);

    try {
      const { data: inserted, error } = await supabase.from("chat_messages").insert({
        conversation_id: selectedConv.id,
        sender_profile_id: profileId,
        sender_type: "department",
        message_type: "text",
        content,
      }).select("id").single();

      if (error) {
        showToast("發送失敗: " + error.message);
        setMessages((prev) => prev.filter((m) => m.id !== tempMsg.id));
      } else if (inserted) {
        setMessages((prev) => prev.map((m) => m.id === tempMsg.id ? { ...m, id: inserted.id } : m));
      }
    } catch (err: any) {
      showToast(err?.message || "發送失敗");
      setMessages((prev) => prev.filter((m) => m.id !== tempMsg.id));
    }
  };

  // Real-time subscription
  useEffect(() => {
    if (!selectedConv?.id) return;

    const channel = supabase
      .channel(`manager-chat-${selectedConv.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "chat_messages",
          filter: `conversation_id=eq.${selectedConv.id}`,
        },
        async (payload) => {
          const newMsg = payload.new as ChatMessage;
          // Skip own messages — optimistic update handles display
          if (newMsg.sender_profile_id === profileId) return;
          if (profileId) {
            supabase.from("chat_message_reads").upsert({
              message_id: newMsg.id,
              reader_profile_id: profileId,
            }, {
              onConflict: "message_id, reader_profile_id",
              ignoreDuplicates: true,
            }).then(() => {});
          }
          // Incremental fetch: only get new messages
          fetchNewMessages(selectedConv.id);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedConv?.id, profileId, fetchNewMessages]);

  // 5-second polling as reliable fallback
  useEffect(() => {
    if (!selectedConv?.id) return;

    const interval = setInterval(() => {
      fetchNewMessages(selectedConv.id);
    }, 5000);

    return () => clearInterval(interval);
  }, [selectedConv?.id, fetchNewMessages]);

  // 10-second polling to refresh conversation list (previews, unread counts, order)
  useEffect(() => {
    if (!activeDept?.id) return;

    const interval = setInterval(() => {
      fetchConversations(activeDept.id);
    }, 10000);

    return () => clearInterval(interval);
  }, [activeDept?.id, fetchConversations]);

  // Fetch all department unread counts (lightweight global polling)
  const fetchAllDeptUnreadCounts = useCallback(async () => {
    if (!profileId) return;
    try {
      let deptIds: string[] = [];

      if (isAdmin) {
        const { data } = await supabase.from("departments").select("id");
        deptIds = (data || []).map((d) => d.id);
      } else {
        const { data } = await supabase
          .from("manager_departments")
          .select("department_id")
          .eq("manager_profile_id", profileId);
        deptIds = (data || []).map((d) => d.department_id);
      }

      if (deptIds.length === 0) return;

      const { data: unreadData } = await supabase
        .from("department_conversations")
        .select("department_id, unread_manager_count")
        .in("department_id", deptIds);

      const counts: Record<string, number> = {};
      (unreadData || []).forEach((row: any) => {
        counts[row.department_id] = (counts[row.department_id] || 0) + (row.unread_manager_count || 0);
      });
      setDeptUnreadCounts((prev) => {
        const next = { ...prev, ...counts };
        // 即時廣播給 AppShell 導航徽章
        const allTotal = Object.values(next).reduce((s, n) => s + (n || 0), 0);
        window.dispatchEvent(new CustomEvent("manager:unread-sync", { detail: { total: allTotal } }));
        return next;
      });
    } catch {
      // Silently fail for background polling
    }
  }, [profileId, isAdmin]);

  // Global 10-second polling: refresh ALL department unread counts
  useEffect(() => {
    if (!profileId || (!isManager && !isAdmin)) return;

    // Run immediately on mount
    fetchAllDeptUnreadCounts();

    const interval = setInterval(() => {
      fetchAllDeptUnreadCounts();
    }, 10000);

    return () => clearInterval(interval);
  }, [profileId, isManager, isAdmin, fetchAllDeptUnreadCounts]);

  // Update department unread counts whenever conversations change
  useEffect(() => {
    if (!activeDept?.id) return;
    const total = conversations.reduce((sum, c) => sum + (c.unread_manager_count || 0), 0);
    setDeptUnreadCounts((prev) => {
      const next = { ...prev, [activeDept.id]: total };
      // 即時廣播給 AppShell 導航徽章
      const allTotal = Object.values(next).reduce((s, n) => s + (n || 0), 0);
      window.dispatchEvent(new CustomEvent("manager:unread-sync", { detail: { total: allTotal } }));
      return next;
    });
  }, [conversations, activeDept?.id]);

  // Load departments on mount
  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  // When department is selected
  useEffect(() => {
    if (!activeDept?.id) return;
    fetchConversations(activeDept.id);
  }, [activeDept?.id, fetchConversations]);

  // When conversation is selected
  useEffect(() => {
    if (!selectedConv?.id) return;
    fetchMessages(selectedConv.id);
    markAsRead(selectedConv.id);
  }, [selectedConv?.id, fetchMessages, markAsRead]);

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Filter conversations
  const filteredConversations = conversations.filter((c) => {
    const matchesStatus = statusFilter === "all" || c.status === statusFilter;
    const matchesSearch = !searchQuery ||
      `${c.employee_name} ${c.sales_specialist || ""}`.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSpecialist = !activeDept?.name.includes("銷售") || specialistFilter === "all"
      || (c.sales_specialist || "") === specialistFilter;
    return matchesStatus && matchesSearch && matchesSpecialist;
  });

  const handleDepartmentSelect = (dept: Department) => {
    setSpecialistFilter("all");
    setActiveDept(dept);
    setSelectedConv(null);
    setMessages([]);
    setExpandedImages(new Set());
    setMobileView("conversations");
  };

  const handleConversationSelect = (conv: Conversation) => {
    setSelectedConv(conv);
    setMobileView("chat");
  };

  if (!isManager && !isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-foreground-400">
        <i className="ri-lock-line text-4xl mb-3" />
        <p className="text-sm font-medium">無權限</p>
        <p className="text-xs mt-1">只有主管可以進入此頁面</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col -m-4 md:-m-6 overflow-hidden">
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 px-4 py-2 rounded-lg bg-foreground-800 text-background-50 text-sm shadow-lg flex items-center gap-2">
          <i className="ri-error-warning-line" />
          {toast}
        </div>
      )}

      {/* Header */}
      <div className="px-4 md:px-6 py-3 border-b border-background-200 flex items-center justify-between shrink-0">
        <div>
          <h1 className="text-lg font-bold text-foreground-900" style={{ fontFamily: "var(--font-heading)" }}>
            主管中心
          </h1>
          <p className="text-xs text-foreground-500">
            <span className="xl:hidden">
              {mobileView === "departments" && "選擇要管理的部門"}
              {mobileView === "conversations" && `${activeDept?.name || "部門"}・選擇員工對話`}
              {mobileView === "chat" && `${activeDept?.name || "部門"}・回覆員工訊息`}
            </span>
            <span className="hidden xl:inline">查看並管理所有部門的員工對話</span>
          </p>
        </div>
      </div>

      {activeDept?.name.includes("銷售") && (
        <div className="px-4 md:px-6 py-3 border-b border-background-200 shrink-0">
          <p className="text-xs text-foreground-500 mb-2">專員切換 · 未讀訊息</p>
          <div className="flex flex-wrap gap-2" aria-label="銷售專員切換">
            {[{ value: "all", label: "全部" }, ...SALES_SPECIALISTS.map((name) => ({ value: name, label: name })), { value: "", label: "部門一般對話" }].map(({ value, label }) => {
              const unread = conversations.reduce((total, conv) => total + (
                value === "all" || (conv.sales_specialist || "") === value
                  ? Number(conv.unread_manager_count) || 0 : 0
              ), 0);
              return (
                <button
                  key={value}
                  type="button"
                  aria-pressed={specialistFilter === value}
                  onClick={() => {
                    setSpecialistFilter(value);
                    setSelectedConv(null);
                    setMessages([]);
                    setExpandedImages(new Set());
                    setMobileView("conversations");
                  }}
                  className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs transition-colors cursor-pointer ${specialistFilter === value ? "border-primary-500 bg-primary-50 text-primary-700" : "border-background-200 text-foreground-600 hover:bg-background-100"}`}
                >
                  {label}
                  <span aria-label={`${unread} 則未讀`} className={`min-w-5 h-5 px-1 rounded-full inline-flex items-center justify-center text-[10px] font-bold ${unread > 0 ? "bg-accent-500 text-white" : "bg-background-100 text-foreground-400"}`}>
                    {unread}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Three Column Layout */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* Left: Department Selector */}
        <div className={`${mobileView === "departments" ? "flex" : "hidden"} xl:flex w-full xl:w-56 shrink-0 border-r border-background-200 flex-col bg-background-50/50`}>
          <div className="px-3 py-3 border-b border-background-200">
            <span className="text-xs font-medium text-foreground-500 uppercase tracking-wider">
              <i className="ri-building-line mr-1" />
              管理部門
            </span>
          </div>
          <div className="flex-1 overflow-y-auto py-2 px-2 space-y-1">
            {loadingDepts ? (
              <div className="p-3 text-center">
                <i className="ri-loader-4-line animate-spin text-foreground-400" />
              </div>
            ) : departments.length === 0 ? (
              <div className="p-3 text-center text-xs text-foreground-400">
                尚無管理的部門
              </div>
            ) : (
              departments.map((dept) => {
                const unread = deptUnreadCounts?.[dept.id] || 0;
                return (
                  <button
                    key={dept.id}
                    onClick={() => handleDepartmentSelect(dept)}
                    className={`w-full flex items-center gap-3 px-3 py-3.5 xl:gap-2 xl:py-2.5 rounded-lg text-sm transition-colors cursor-pointer ${
                      activeDept?.id === dept.id
                        ? "bg-primary-50 text-primary-700"
                        : "text-foreground-600 hover:bg-background-100"
                    }`}
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={`${dept.icon} ${activeDept?.id === dept.id ? "text-primary-500" : "text-foreground-400"}`} />
                    </div>
                    <span className="flex-1 text-left">{dept.name}</span>
                    {unread > 0 && (
                      <span className="min-w-[1.25rem] h-5 px-1 rounded-full bg-accent-500 text-background-50 text-[10px] flex items-center justify-center font-bold">
                        {unread > 99 ? "99+" : unread}
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Middle: Conversation List */}
        <div className={`${mobileView === "conversations" ? "flex" : "hidden"} xl:flex w-full xl:w-72 shrink-0 border-r border-background-200 flex-col bg-background-50/50`}>
          {activeDept ? (
            <>
              <div className="px-3 py-3 border-b border-background-200 space-y-2">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setMobileView("departments")}
                    className="xl:hidden w-10 h-10 -ml-1 rounded-lg flex items-center justify-center text-foreground-600 hover:bg-background-100 active:bg-background-200"
                    aria-label="返回部門列表"
                  >
                    <i className="ri-arrow-left-line text-lg" />
                  </button>
                  <div className="min-w-0">
                    <h2 className="text-sm font-semibold text-foreground-900 truncate">{activeDept.name} 的對話</h2>
                    <p className="xl:hidden text-[11px] text-foreground-400">點選員工以開啟聊天</p>
                  </div>
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="搜尋員工名稱..."
                  className="w-full px-3 py-2.5 xl:px-2.5 xl:py-1.5 rounded-lg border border-background-200 text-sm xl:text-xs text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 bg-background-50"
                />
                <div className="flex gap-1 flex-wrap">
                  {(["all", "waiting", "processing", "resolved", "closed"] as const).map((s) => (
                    <button
                      key={s}
                      onClick={() => setStatusFilter(s)}
                      className={`px-3 xl:px-2 py-2 xl:py-1 rounded-full text-xs xl:text-[10px] font-medium cursor-pointer whitespace-nowrap transition-colors ${
                        statusFilter === s
                          ? "bg-primary-500 text-background-50"
                          : "bg-background-100 text-foreground-500 hover:bg-background-200"
                      }`}
                    >
                      {s === "all" ? "全部" : STATUS_LABELS[s]}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex-1 overflow-y-auto">
                {loadingConvs ? (
                  <div className="p-4 text-center">
                    <i className="ri-loader-4-line animate-spin text-foreground-400" />
                  </div>
                ) : filteredConversations.length === 0 ? (
                  <div className="p-4 text-center text-xs text-foreground-400">
                    尚無對話
                  </div>
                ) : (
                  filteredConversations.map((conv) => (
                    <button
                      key={conv.id}
                      onClick={() => handleConversationSelect(conv)}
                      className={`w-full text-left px-4 xl:px-3 py-4 xl:py-3 border-b border-background-200/50 transition-colors cursor-pointer ${
                        selectedConv?.id === conv.id
                          ? "bg-primary-50"
                          : "hover:bg-background-100"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-foreground-900">{conv.employee_name}{conv.sales_specialist && <span className="block text-xs font-normal text-primary-600 mt-1">專員：{conv.sales_specialist}</span>}</span>
                        {conv.unread_manager_count > 0 && (
                          <span className="w-5 h-5 rounded-full bg-accent-500 text-background-50 text-[10px] flex items-center justify-center font-bold">
                            {conv.unread_manager_count}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-foreground-500 truncate mb-1.5">
                        {conv.last_message_preview || "尚無訊息"}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${STATUS_COLORS[conv.status]}`}>
                          {STATUS_LABELS[conv.status]}
                        </span>
                        {conv.assigned_manager_id === profileId && (
                          <span className="text-[10px] text-primary-600 font-medium">
                            <i className="ri-user-star-line mr-0.5" />
                            已接手
                          </span>
                        )}
                        {conv.last_message_at && (
                          <span className="text-[10px] text-foreground-400">
                            {formatTime(conv.last_message_at)}
                          </span>
                        )}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-foreground-400 px-4 text-center">
              <i className="ri-building-line text-3xl mb-2" />
              <p className="text-xs">選擇左側部門查看對話</p>
            </div>
          )}
        </div>

        {/* Right: Chat Window */}
        <div className={`${mobileView === "chat" ? "flex" : "hidden"} xl:flex flex-1 flex-col min-w-0 bg-background-50`}>
          {selectedConv ? (
            <>
              {/* Chat Header */}
              <div className="px-3 xl:px-4 py-2.5 xl:py-3 border-b border-background-200 flex flex-col xl:flex-row xl:items-center xl:justify-between gap-2 shrink-0">
                <div className="flex items-center gap-2 xl:gap-3 min-w-0">
                  <button
                    type="button"
                    onClick={() => setMobileView("conversations")}
                    className="xl:hidden w-10 h-10 -ml-1 rounded-lg flex items-center justify-center shrink-0 text-foreground-600 hover:bg-background-100 active:bg-background-200"
                    aria-label="返回對話列表"
                  >
                    <i className="ri-arrow-left-line text-lg" />
                  </button>
                  <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center text-background-50 text-xs font-semibold shrink-0">
                    {selectedConv.employee_name.charAt(0)}
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-foreground-900 truncate">{selectedConv.employee_name}{selectedConv.sales_specialist ? ` · 專員：${selectedConv.sales_specialist}` : ""}</h3>
                    <p className="text-xs text-foreground-500 truncate">
                      {selectedConv.assigned_manager_id === profileId ? "已接手・" : ""}
                      以 {activeDept?.name} 身份回覆
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 pl-11 xl:pl-0">
                  <span className={`hidden xl:inline-flex text-xs px-2 py-1 rounded-full font-medium ${STATUS_COLORS[selectedConv.status]}`}>
                    {STATUS_LABELS[selectedConv.status]}
                  </span>
                  {/* Status Dropdown */}
                  <select
                    value={selectedConv.status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    className="flex-1 xl:flex-none min-w-0 text-sm xl:text-xs px-3 py-2.5 xl:px-2 xl:py-1 rounded-lg border border-background-200 bg-background-50 text-foreground-700 focus:outline-none cursor-pointer"
                  >
                    <option value="waiting">等待處理</option>
                    <option value="processing">處理中</option>
                    <option value="resolved">已解決</option>
                    <option value="closed">已關閉</option>
                  </select>
                  {selectedConv.assigned_manager_id !== profileId && (
                    <button
                      onClick={handleAssign}
                      className="min-h-10 px-4 xl:px-3 xl:min-h-0 xl:py-1.5 rounded-lg bg-primary-500 text-background-50 text-sm xl:text-xs font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
                    >
                      <i className="ri-hand-heart-line" />
                      接手
                    </button>
                  )}
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-3 xl:px-4 py-4 space-y-4 min-h-0 overscroll-contain">
                {loadingMsgs ? (
                  <div className="flex flex-col items-center justify-center h-full">
                    <i className="ri-loader-4-line animate-spin text-primary-500 text-2xl" />
                    <p className="text-sm text-foreground-500 mt-2">載入訊息...</p>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-foreground-400">
                    <i className="ri-chat-smile-3-line text-4xl mb-3" />
                    <p className="text-sm font-medium">尚無訊息</p>
                    <p className="text-xs mt-1">發送第一則訊息給 {selectedConv.employee_name}</p>
                  </div>
                ) : (
                  messages.map((msg) => {
                    const isMe = msg.sender_type === "department" && msg.sender_profile_id === profileId;
                    const isSystem = msg.message_type === "system";
                    const displayName = msg.sender_type === "department"
                      ? (msg.department_name || activeDept?.name || "部門")
                      : (msg.sender_name || selectedConv.employee_name || "員工");

                    if (isSystem) {
                      return (
                        <div key={msg.id} className="flex justify-center">
                          <span className="text-xs text-foreground-400 bg-background-100 px-3 py-1 rounded-full">
                            {msg.content}
                          </span>
                        </div>
                      );
                    }

                    return (
                      <div key={msg.id} className={`flex gap-2 xl:gap-3 ${isMe ? "flex-row-reverse" : ""}`}>
                        {!isMe && (
                          <div className="w-8 h-8 rounded-full bg-secondary-500 flex items-center justify-center text-background-50 text-xs font-semibold shrink-0">
                            {displayName.charAt(0)}
                          </div>
                        )}
                        <div className={`flex-1 min-w-0 ${isMe ? "text-right" : ""}`}>
                          <div className={`flex items-center gap-2 mb-1 ${isMe ? "justify-end" : ""}`}>
                            <span className="text-xs font-medium text-foreground-700">{displayName}</span>
                            <span className="text-[10px] text-foreground-400">{formatTime(msg.created_at)}</span>
                            {isManager && !msg.id.startsWith("temp-") && (
                              <button type="button" onClick={() => handleDeleteMessage(msg)} disabled={deletingId !== null}
                                aria-label="刪除訊息" title="刪除訊息"
                                className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-accent-600 hover:bg-accent-50 disabled:opacity-40">
                                <i className={deletingId === msg.id ? "ri-loader-4-line animate-spin" : "ri-delete-bin-line"} />
                                刪除
                              </button>
                            )}                            {msg.is_edited && <span className="text-[10px] text-foreground-400">(已編輯)</span>}
                          </div>
                      <div
                            className={`inline-block px-3 py-2 rounded-xl text-sm text-left ${
                              isMe
                                ? "bg-primary-500 text-background-50 rounded-br-none"
                                : "bg-background-100 text-foreground-800 rounded-bl-none"
                            }`}
                          >
                            {msg.message_type === "image" ? (
                              <img
                                src={msg.content}
                                alt="圖片"
                                className={`rounded-lg cursor-pointer transition-all ${
                                  expandedImages.has(msg.id)
                                    ? "max-w-full max-h-[70vh] object-contain"
                                    : "max-w-[200px] max-h-[200px] object-cover"
                                }`}
                                loading="lazy"
                                onClick={() => toggleImageExpand(msg.id)}
                              />
                            ) : msg.message_type === "file" ? (
                              <span className="text-background-50/80 italic">[檔案]</span>
                            ) : (
                              <p className="whitespace-pre-wrap break-words max-w-[78vw] xl:max-w-none">{msg.content}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              {selectedConv.status === "closed" ? (
                <div className="px-4 py-3 border-t border-background-200 text-center shrink-0">
                  <p className="text-sm text-foreground-500">此對話已關閉，無法再發送訊息</p>
                </div>
              ) : (
                <div className="px-2 xl:px-4 pt-2 xl:py-3 pb-[max(0.5rem,env(safe-area-inset-bottom))] border-t border-background-200 shrink-0 bg-background-50">
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      accept="image/*"
                      ref={fileInputRef}
                      onChange={handleImageSelect}
                      className="hidden"
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="w-10 h-10 xl:w-8 xl:h-8 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-500 cursor-pointer shrink-0"
                      aria-label="傳送圖片"
                    >
                      <i className="ri-image-line" />
                    </button>
                    <button className="hidden xl:flex w-8 h-8 items-center justify-center rounded-lg hover:bg-background-100 text-foreground-400 cursor-pointer">
                      <i className="ri-attachment-line" />
                    </button>
                    <input
                      type="text"
                      value={inputText}
                      onChange={(e) => setInputText(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleSend()}
                      placeholder={`以 ${activeDept?.name} 身份回覆...`}
                      className="flex-1 min-w-0 h-10 px-3 py-2 rounded-lg border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 bg-background-50"
                    />
                    <button
                      onClick={handleSend}
                      disabled={!inputText.trim()}
                      className="w-10 h-10 xl:w-auto xl:h-auto xl:px-4 xl:py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-40 cursor-pointer whitespace-nowrap flex items-center justify-center gap-1 shrink-0"
                      aria-label="送出訊息"
                    >
                      <i className="ri-send-plane-line" />
                      <span className="hidden xl:inline">送出</span>
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-foreground-400">
              <i className="ri-message-3-line text-4xl mb-3" />
              <p className="text-sm font-medium">選擇一個對話開始回覆</p>
              <p className="text-xs mt-1">點選中間的對話列表即可查看員工訊息</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
